import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flame, Plus, X as XIcon, Sparkles, Loader2, ChefHat, Lock, History, Trash2, Heart, Camera } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_INGREDIENTS = 6;
const MIN_INGREDIENTS = 2;
const LOG_KEY = "omnimuscle_food_log_v1";

const DIETS = [
  { id: "any", label: "Any" },
  { id: "high-protein", label: "High Protein" },
  { id: "vegan", label: "Vegan" },
  { id: "vegetarian", label: "Vegetarian" },
  { id: "keto", label: "Keto" },
  { id: "low-carb", label: "Low Carb" },
  { id: "gluten-free", label: "Gluten-Free" },
];
const CUISINES = [
  { id: "any", label: "Any" },
  { id: "mexican", label: "Mexican" },
  { id: "asian", label: "Asian" },
  { id: "italian", label: "Italian" },
  { id: "mediterranean", label: "Mediterranean" },
  { id: "american", label: "American" },
  { id: "indian", label: "Indian" },
];
const TIMES = [
  { id: "any", label: "Any" },
  { id: "15", label: "≤15 min" },
  { id: "30", label: "≤30 min" },
  { id: "60", label: "≤60 min" },
];

const SUGGESTIONS = [
  "chicken breast", "rice", "eggs", "broccoli", "spinach", "salmon",
  "avocado", "sweet potato", "ground beef", "tofu", "quinoa", "kale",
  "black beans", "pasta", "tomato", "garlic",
];

// PT date (America/Los_Angeles). All daily items in OmniMuscle reset at
// 00:00 PT — never UTC. Without this, the Cauldron log lookups roll over at
// 5pm PT (== 00:00 UTC during PDT).
const todayKey = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
};

/**
 * Phoenix Cauldron 🐦‍🔥🍳
 * Drop in 2-6 ingredients → AI returns 2-3 recipe ideas using Gemini.
 * Free: 3/day. Premium: unlimited.
 *
 * Visual metaphor: a mystical pot with bubbling flames. Ingredient chips
 * stack inside; pressing COOK plays a flame animation while the AI thinks,
 * then reveals recipe cards.
 */
export default function PhoenixCauldron() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [input, setInput] = useState("");
  const [diet, setDiet] = useState("any");
  const [cuisine, setCuisine] = useState("any");
  const [timeMax, setTimeMax] = useState("any");
  const [cooking, setCooking] = useState(false);
  const [results, setResults] = useState(null);
  // 📜 Past cooks — fetched from /api/cauldron/history (newest first)
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [favCount, setFavCount] = useState(0);
  const [favOnly, setFavOnly] = useState(false);
  // 📸 Snap-the-pot photo scan state
  const [scanning, setScanning] = useState(false);

  async function loadState() {
    if (!user) {
      setState(null);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/cauldron/state`);
      setState(data);
    } catch {
      setState(null);
    }
  }

  async function loadHistory() {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/cauldron/history?limit=25`);
      setHistory(data?.items || []);
      setFavCount(Number(data?.favorite_count) || 0);
      setHistoryLoaded(true);
    } catch {
      setHistory([]);
    }
  }

  async function deleteHistoryItem(cookId) {
    setHistory((cur) => cur.filter((c) => c.id !== cookId));
    try {
      await axios.delete(`${API}/cauldron/history/${cookId}`);
      toast.success("Cook removed from history");
    } catch {
      toast.error("Couldn't delete — refresh and try again.");
      loadHistory();
    }
  }

  async function toggleFavorite(cookId, currentlyFav) {
    const next = !currentlyFav;
    // Optimistic update
    setHistory((cur) => cur.map((c) => (c.id === cookId ? { ...c, favorite: next } : c)));
    try {
      await axios.post(`${API}/cauldron/history/${cookId}/favorite`, { favorite: next });
      // Re-fetch so favorites get re-sorted to the top
      loadHistory();
    } catch {
      // revert
      setHistory((cur) => cur.map((c) => (c.id === cookId ? { ...c, favorite: currentlyFav } : c)));
      toast.error("Couldn't update favorite — try again.");
    }
  }

  useEffect(() => { loadState(); }, [user]);

  function addIngredient(raw) {
    const v = (raw || input).trim().toLowerCase();
    if (!v) return;
    if (ingredients.length >= MAX_INGREDIENTS) {
      toast.error(`Max ${MAX_INGREDIENTS} ingredients per cook.`);
      return;
    }
    if (ingredients.includes(v)) {
      toast.info("Already in the pot 🍳");
      return;
    }
    setIngredients([...ingredients, v]);
    setInput("");
  }

  function removeIngredient(i) {
    setIngredients(ingredients.filter((_, idx) => idx !== i));
  }

  function clearAll() {
    setIngredients([]);
    setResults(null);
  }

  // 📸🍲 Snap-the-pot — user picks/captures a photo of ingredients,
  // Gemini reads them, we merge the list into the chip stack (deduped,
  // capped at MAX_INGREDIENTS).
  async function onScanPhoto(event) {
    const file = event.target.files?.[0];
    // Reset input so the same file can be re-uploaded later
    event.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error("Use a JPEG, PNG, or WEBP photo.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo is too large — under 5 MB please.");
      return;
    }
    setScanning(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const s = String(reader.result || "");
          const comma = s.indexOf(",");
          resolve(comma >= 0 ? s.slice(comma + 1) : s);
        };
        reader.onerror = () => reject(new Error("read_failed"));
        reader.readAsDataURL(file);
      });
      const { data } = await axios.post(`${API}/cauldron/scan-ingredients`, {
        image_base64: b64,
        mime: file.type,
      });
      const detected = (data.ingredients || []).map((s) => String(s).trim().toLowerCase()).filter(Boolean);
      if (detected.length === 0) {
        toast.error("Couldn't spot any ingredients. Try better lighting or a closer shot.");
        return;
      }
      let added = 0;
      let skipped = 0;
      setIngredients((cur) => {
        const merged = [...cur];
        for (const name of detected) {
          if (merged.length >= MAX_INGREDIENTS) { skipped += (detected.length - added); break; }
          if (!merged.includes(name)) {
            merged.push(name);
            added += 1;
          } else {
            skipped += 1;
          }
        }
        return merged;
      });
      toast.success(`📸🍲 ${added} ingredient${added === 1 ? "" : "s"} added to the pot`, {
        description: skipped > 0
          ? `${skipped} skipped (duplicate or pot full)${data.remaining_today != null ? ` · ${data.remaining_today} scans left today` : ""}`
          : (data.remaining_today != null ? `${data.remaining_today} scans left today` : undefined),
        duration: 4500,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.error === "premium_required") {
        toast.error(detail.message || "Daily free scans used up — upgrade for unlimited.");
      } else if (e?.response?.status === 400) {
        toast.error(typeof detail === "string" ? detail : "Photo couldn't be read.");
      } else {
        toast.error("Scan failed — try again in a moment.");
      }
    } finally {
      setScanning(false);
    }
  }

  async function cook() {
    if (cooking) return;
    if (ingredients.length < MIN_INGREDIENTS) {
      toast.error(`Add at least ${MIN_INGREDIENTS} ingredients.`);
      return;
    }
    setCooking(true);
    setResults(null);
    try {
      const { data } = await axios.post(`${API}/cauldron/cook`, {
        ingredients,
        diet,
        cuisine,
        time_max: timeMax,
      });
      setResults(data.recipes || []);
      toast.success(`🐦‍🔥 ${data.recipes?.length || 0} recipe${data.recipes?.length === 1 ? "" : "s"} fired up!`, {
        description: data.remaining_today != null ? `${data.remaining_today} free cooks left today` : undefined,
        duration: 4500,
      });
      // 💋 Chef's Kiss + 💀 Life Ash drops — celebrate the crit
      if (data.chefs_kiss) {
        toast.success("💋 CHEF'S KISS! Perfect recipe — +1 💋 collected", {
          description: `+${data.life_ash_dropped || 0} 💀 Life Ash bonus`,
          duration: 6000,
        });
      } else if (data.life_ash_dropped > 0) {
        toast.success(`💀 +${data.life_ash_dropped} Life Ash dropped from the cauldron`, { duration: 4000 });
      }
      // 🦅 Pet food drop — every cook gives at least 1 XP Berry to feed your phoenix
      if (data.pet_food_drop) {
        const parts = [];
        if (data.pet_food_drop.xp_berry) parts.push(`🫐 +${data.pet_food_drop.xp_berry}`);
        if (data.pet_food_drop.roasted_plant) parts.push(`🍃 +${data.pet_food_drop.roasted_plant}`);
        if (data.pet_food_drop.rainbow_milk) parts.push(`🥛 +${data.pet_food_drop.rainbow_milk}`);
        if (parts.length > 0) {
          toast.success(`🦅 Pet food drop: ${parts.join(" · ")}`, { duration: 3500 });
        }
      }
      await loadState();
      // Refresh history so the new cook shows up in "Recent Cooks"
      if (historyOpen || historyLoaded) await loadHistory();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail?.error === "premium_required") {
        toast.error(detail.message || "Premium required.");
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "Cauldron failed. Try again with different ingredients.");
      }
    } finally {
      setCooking(false);
    }
  }

  // "Add to today's log" — pushes the recipe to FoodLog's localStorage
  function addRecipeToFoodLog(r) {
    try {
      const raw = localStorage.getItem(LOG_KEY);
      const log = raw ? JSON.parse(raw) : [];
      const m = r.macros_per_serving || {};
      log.push({
        id: `cauldron-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        date: todayKey(),
        meal: "Snack",  // user can re-assign via the new meal-move feature
        name: r.title,
        servings: 1,
        cal: Number(m.calories) || 0,
        p: Number(m.protein_g) || 0,
        c: Number(m.carbs_g) || 0,
        f: Number(m.fat_g) || 0,
      });
      localStorage.setItem(LOG_KEY, JSON.stringify(log));
      toast.success(`Added "${r.title}" to today's log`, {
        description: "Tap the meal dropdown to move it from Snack to another column.",
      });
      // Trigger a window focus event so FoodLog re-reads localStorage
      window.dispatchEvent(new Event("focus"));
    } catch {
      toast.error("Couldn't save to today's log.");
    }
  }

  const remainingDisplay = useMemo(() => {
    if (!state) return null;
    if (state.is_premium) return "Unlimited";
    return `${state.remaining_today}/${state.free_per_day} left today`;
  }, [state]);

  if (!user) {
    return (
      <div className="bg-[#0F0F0F] border-2 border-zinc-800 p-6 text-center" data-testid="cauldron-locked">
        <div className="text-5xl mb-2">🐦‍🔥</div>
        <h3 className="font-display text-2xl text-white mb-1">Phoenix Cauldron</h3>
        <p className="text-sm text-neutral-500">Sign in to fire up the stove and AI-cook with your ingredients.</p>
      </div>
    );
  }

  return (
    <div
      data-testid="phoenix-cauldron"
      className="bg-gradient-to-br from-[#1a0d2b] via-[#0F0F0F] to-[#3a0a0a] border-2 border-[#D4AF37]/70 p-6 relative overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-orange-500 opacity-10 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1 flex items-center gap-1.5">
          <Flame size={11} /> Phoenix Cauldron
          {state && !state.is_premium && (
            <span className="ml-2 text-[9px] text-neutral-500 normal-case tracking-normal">{remainingDisplay}</span>
          )}
        </div>
        <h2 className="font-display text-3xl text-white">
          What's in the <span className="text-[#D4AF37]">Pot?</span>
        </h2>
        <p className="text-xs text-neutral-400 mt-1 mb-5 max-w-md">
          Drop {MIN_INGREDIENTS}–{MAX_INGREDIENTS} ingredients in the cauldron. The Phoenix Chef 🐦‍🔥 will conjure 2–3 recipes using only what you have.
        </p>

        {/* The Cauldron visual */}
        <div className="relative my-4">
          <CauldronPot
            ingredients={ingredients}
            cooking={cooking}
            onRemove={removeIngredient}
          />
        </div>

        {/* Ingredient input */}
        <div className="flex gap-2 mt-4">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addIngredient())}
            placeholder="e.g., chicken breast, spinach, eggs..."
            className="input-omni flex-1 text-sm"
            disabled={cooking || ingredients.length >= MAX_INGREDIENTS}
            data-testid="cauldron-input"
            list="cauldron-suggest"
          />
          <datalist id="cauldron-suggest">
            {SUGGESTIONS.map((s) => <option key={s} value={s} />)}
          </datalist>
          <button
            type="button"
            onClick={() => addIngredient()}
            disabled={cooking || !input.trim() || ingredients.length >= MAX_INGREDIENTS}
            data-testid="cauldron-add"
            className="btn-omni btn-outline-gold text-xs uppercase tracking-widest disabled:opacity-40"
          >
            <Plus size={14} /> Add
          </button>
        </div>

        {/* 📸🍲 Snap the pot — photo → auto-detect ingredients */}
        <div className="mt-2">
          <label
            data-testid="cauldron-scan-label"
            className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-widest border border-dashed border-zinc-800 hover:border-[#D4AF37] text-neutral-400 hover:text-[#D4AF37] px-3 py-2 cursor-pointer transition-colors ${
              (scanning || cooking || ingredients.length >= MAX_INGREDIENTS) ? "opacity-50 pointer-events-none" : ""
            }`}
          >
            {scanning ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
            {scanning ? "Reading the pot…" : "📸 Snap the pot"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="sr-only"
              onChange={onScanPhoto}
              disabled={scanning || cooking || ingredients.length >= MAX_INGREDIENTS}
              data-testid="cauldron-scan-input"
            />
          </label>
          <p className="text-[10px] text-neutral-600 mt-1 italic">
            Photo of your ingredients → auto-fills the chips → hit COOK.
          </p>
        </div>

        {/* Quick add chips */}
        {ingredients.length < MAX_INGREDIENTS && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            <span className="text-[10px] uppercase tracking-widest text-neutral-600 self-center mr-1">Quick add:</span>
            {SUGGESTIONS.filter((s) => !ingredients.includes(s)).slice(0, 8).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => addIngredient(s)}
                disabled={cooking}
                className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#D4AF37] border border-zinc-800 hover:border-[#D4AF37] px-2 py-1"
              >
                + {s}
              </button>
            ))}
          </div>
        )}

        {/* Constraints */}
        <div className="grid grid-cols-3 gap-2 mt-5">
          <ConstraintSelect label="Diet" value={diet} onChange={setDiet} options={DIETS} testId="cauldron-diet" />
          <ConstraintSelect label="Cuisine" value={cuisine} onChange={setCuisine} options={CUISINES} testId="cauldron-cuisine" />
          <ConstraintSelect label="Time" value={timeMax} onChange={setTimeMax} options={TIMES} testId="cauldron-time" />
        </div>

        {/* Cook button + actions */}
        <div className="flex items-center justify-between gap-2 mt-5">
          <button
            type="button"
            onClick={clearAll}
            disabled={cooking || ingredients.length === 0}
            className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white disabled:opacity-30"
          >
            Clear pot
          </button>
          <button
            type="button"
            data-testid="cauldron-cook-btn"
            onClick={cook}
            disabled={cooking || ingredients.length < MIN_INGREDIENTS || (state && !state.is_premium && state.remaining_today === 0)}
            className="bg-gradient-to-r from-orange-600 to-[#D4AF37] hover:brightness-110 text-black font-bold uppercase tracking-[0.2em] text-sm px-8 py-3 transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {cooking ? (
              <><Loader2 size={14} className="animate-spin" /> Cooking…</>
            ) : state && !state.is_premium && state.remaining_today === 0 ? (
              <><Lock size={14} /> Daily limit reached</>
            ) : (
              <><Flame size={14} /> Cook 🔥</>
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {results && results.length > 0 && (
        <div className="mt-6 pt-6 border-t border-zinc-800/60 space-y-4" data-testid="cauldron-results">
          <h3 className="font-display text-2xl text-white">
            <span className="text-[#D4AF37]">{results.length}</span> Phoenix Recipe{results.length === 1 ? "" : "s"} 🐦‍🔥
          </h3>
          {results.map((r, i) => (
            <RecipeCard key={i} recipe={r} onAddToLog={() => addRecipeToFoodLog(r)} />
          ))}
          <p className="text-[10px] uppercase tracking-widest text-neutral-600 text-center pt-2">
            Macros are AI estimates · Always confirm with your scale & labels
          </p>
        </div>
      )}

      {/* 📜 My Cauldron Recipes — historical cooks (newest first) */}
      <div className="mt-6 pt-5 border-t border-zinc-800/60 relative">
        <button
          type="button"
          data-testid="cauldron-history-toggle"
          onClick={async () => {
            const next = !historyOpen;
            setHistoryOpen(next);
            if (next && !historyLoaded) await loadHistory();
          }}
          className="w-full flex items-center justify-between text-left group"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold inline-flex items-center gap-1.5">
              <History size={11} /> My Cauldron Recipes
              {favCount > 0 && (
                <span className="ml-1 inline-flex items-center gap-0.5 text-[#D4AF37]">
                  <Heart size={10} className="fill-[#D4AF37]" /> {favCount}
                </span>
              )}
            </div>
            <p className="text-xs text-neutral-500 mt-0.5">
              Your last 25 AI-cooked recipes — re-find a favorite, no re-cook needed.
            </p>
          </div>
          <span className="text-[#D4AF37] text-2xl leading-none group-hover:translate-y-0.5 transition-transform">
            {historyOpen ? "▴" : "▾"}
          </span>
        </button>

        {historyOpen && (
          <div className="mt-4 space-y-3" data-testid="cauldron-history-list">
            {/* Favorites-only filter chip */}
            {historyLoaded && history.length > 0 && (
              <div className="flex items-center justify-between gap-2 pb-1">
                <button
                  type="button"
                  data-testid="cauldron-fav-filter"
                  onClick={() => setFavOnly((s) => !s)}
                  className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5 border px-2.5 py-1 transition-colors ${
                    favOnly
                      ? "bg-[#D4AF37] text-black border-[#D4AF37] font-bold"
                      : "text-neutral-400 border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  }`}
                  aria-pressed={favOnly}
                >
                  <Heart size={10} className={favOnly ? "fill-black" : ""} />
                  {favOnly ? "Favorites only" : "Show favorites only"}
                  {favCount > 0 && (
                    <span className="tabular-nums">· {favCount}</span>
                  )}
                </button>
                {favOnly && (
                  <button
                    type="button"
                    onClick={() => setFavOnly(false)}
                    className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}

            {!historyLoaded ? (
              <div className="text-center text-neutral-500 py-6 text-sm">
                <Loader2 size={13} className="animate-spin inline mr-1.5" /> Loading…
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-neutral-500 text-sm" data-testid="cauldron-history-empty">
                <div className="text-4xl mb-2 opacity-50">🍳</div>
                No cooks yet — fire up the pot above!
              </div>
            ) : (favOnly ? history.filter((h) => h.favorite) : history).length === 0 ? (
              <div className="text-center py-8 text-neutral-500 text-sm" data-testid="cauldron-fav-empty">
                <div className="text-4xl mb-2 opacity-50">💔</div>
                No favorites yet. Tap the heart on a cook to keep it here.
              </div>
            ) : (
              (favOnly ? history.filter((h) => h.favorite) : history).map((h) => (
                <HistoryItem
                  key={h.id}
                  cook={h}
                  onAddToLog={addRecipeToFoodLog}
                  onDelete={() => deleteHistoryItem(h.id)}
                  onToggleFavorite={() => toggleFavorite(h.id, !!h.favorite)}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ Cauldron Pot Visual ============ */
function CauldronPot({ ingredients, cooking, onRemove }) {
  return (
    <div className="relative mx-auto" style={{ width: 280, height: 200 }}>
      {/* Pot SVG */}
      <svg viewBox="0 0 280 200" className="absolute inset-0">
        {/* Handles */}
        <ellipse cx="34" cy="120" rx="10" ry="14" fill="none" stroke="#3a2410" strokeWidth="5" />
        <ellipse cx="246" cy="120" rx="10" ry="14" fill="none" stroke="#3a2410" strokeWidth="5" />
        {/* Pot body */}
        <defs>
          <linearGradient id="potG" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#2a2a2a" />
            <stop offset="60%" stopColor="#1a1a1a" />
            <stop offset="100%" stopColor="#0a0a0a" />
          </linearGradient>
          <radialGradient id="brothG" cx="50%" cy="35%" r="60%">
            <stop offset="0%" stopColor="#FF6A1A" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#D4AF37" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#1a0d2b" stopOpacity="0.0" />
          </radialGradient>
        </defs>
        <path
          d="M 28 100 Q 28 90 40 90 L 240 90 Q 252 90 252 100 L 248 180 Q 246 192 235 192 L 45 192 Q 34 192 32 180 Z"
          fill="url(#potG)"
          stroke="#D4AF37"
          strokeWidth="2"
        />
        {/* Broth / glow */}
        <ellipse cx="140" cy="100" rx="106" ry="14" fill="url(#brothG)" opacity={cooking ? 1 : 0.7} />
        {/* Rim highlight */}
        <ellipse cx="140" cy="98" rx="104" ry="6" fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />
      </svg>

      {/* Flame anim under the pot when cooking */}
      {cooking && (
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-32 h-10 pointer-events-none">
          <div className="cauld-flame absolute inset-x-0 bottom-0 mx-auto w-24 h-10 rounded-full bg-gradient-to-t from-orange-600 via-yellow-400 to-transparent opacity-90 blur-md" />
          <div className="cauld-flame absolute inset-x-0 bottom-0 mx-auto w-12 h-6 rounded-full bg-white opacity-80 blur-sm" style={{ animationDelay: "0.15s" }} />
        </div>
      )}

      {/* Ingredient chips floating inside the pot */}
      <div className="absolute inset-x-6 top-[44px] bottom-[18px] flex flex-wrap items-end content-end justify-center gap-1.5 px-4 pb-2 z-10">
        {ingredients.length === 0 ? (
          <p className="text-[11px] text-neutral-500 italic">Pot is empty. Add ingredients below ↓</p>
        ) : (
          ingredients.map((ing, i) => (
            <span
              key={i}
              data-testid={`cauldron-chip-${i}`}
              className={`group inline-flex items-center gap-1 bg-[#0A0A0A]/85 backdrop-blur-sm border border-[#D4AF37]/70 text-[#D4AF37] text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${cooking ? "cauld-bubble" : ""}`}
              style={{ animationDelay: `${i * 0.15}s` }}
            >
              {ing}
              {!cooking && (
                <button
                  type="button"
                  onClick={() => onRemove && onRemove(i)}
                  data-testid={`cauldron-chip-remove-${i}`}
                  className="opacity-60 hover:opacity-100 hover:text-red-400 transition-colors ml-0.5"
                  aria-label={`Remove ${ing}`}
                ><XIcon size={10} /></button>
              )}
            </span>
          ))
        )}
      </div>

      {/* Floating bubbles when cooking */}
      {cooking && (
        <>
          {[0, 1, 2, 3, 4].map((b) => (
            <span
              key={b}
              className="cauld-rise absolute bottom-12 w-1.5 h-1.5 rounded-full bg-[#D4AF37]"
              style={{
                left: `${30 + b * 16}%`,
                animationDelay: `${b * 0.3}s`,
              }}
            />
          ))}
        </>
      )}

      <style>{`
        @keyframes cauld-flame {
          0%,100% { transform: translateY(0) scaleY(1); opacity: 0.9; }
          50%     { transform: translateY(-3px) scaleY(1.15); opacity: 1; }
        }
        .cauld-flame { animation: cauld-flame 0.4s ease-in-out infinite; }
        @keyframes cauld-bubble {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-3px); }
        }
        .cauld-bubble { animation: cauld-bubble 1.4s ease-in-out infinite; }
        @keyframes cauld-rise {
          0%   { transform: translateY(0)    scale(0.4); opacity: 0; }
          40%  { transform: translateY(-30px) scale(1);   opacity: 0.9; }
          100% { transform: translateY(-90px) scale(0.6); opacity: 0; }
        }
        .cauld-rise { animation: cauld-rise 1.8s ease-out infinite; }
      `}</style>
    </div>
  );
}

/* ============ Constraint Select ============ */
function ConstraintSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">{label}</span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-omni w-full text-xs"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id} className="bg-[#0A0A0A]">{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/* ============ Recipe Card ============ */
function RecipeCard({ recipe, onAddToLog }) {
  const m = recipe.macros_per_serving || {};
  const totalMin = (recipe.prep_min || 0) + (recipe.cook_min || 0);
  const [expanded, setExpanded] = useState(false);
  return (
    <div data-testid="cauldron-recipe-card" className="bg-[#0A0A0A] border border-[#D4AF37]/40 p-4">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex-1 min-w-0">
          <h4 className="font-display text-xl text-white flex items-center gap-2">
            <ChefHat size={16} className="text-[#D4AF37]" /> {recipe.title}
          </h4>
          {recipe.tagline && <p className="text-xs text-neutral-400 italic mt-0.5">{recipe.tagline}</p>}
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-2xl text-[#D4AF37] leading-none tabular-nums">{m.calories || 0}</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">kcal/serv</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-widest text-neutral-500 mt-2">
        <span>⏱ {totalMin}m</span>
        <span>🍽 {recipe.servings} serv</span>
        <span className="text-red-400">P{m.protein_g || 0}g</span>
        <span className="text-green-500">C{m.carbs_g || 0}g</span>
        <span className="text-blue-400">F{m.fat_g || 0}g</span>
      </div>

      {recipe.extras_needed && recipe.extras_needed.length > 0 && (
        <div className="mt-3 text-[11px]">
          <span className="text-neutral-500">Also needed: </span>
          <span className="text-neutral-300">{recipe.extras_needed.join(", ")}</span>
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((s) => !s)}
        className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-white mt-3"
      >
        {expanded ? "Hide steps ▴" : "Show steps ▾"}
      </button>

      {expanded && (
        <ol className="mt-2 space-y-1.5 text-sm text-neutral-300 list-decimal list-inside">
          {(recipe.instructions || []).map((step, i) => (
            <li key={i} className="leading-relaxed">{step}</li>
          ))}
        </ol>
      )}

      <div className="flex gap-2 mt-4">
        <button
          type="button"
          onClick={onAddToLog}
          data-testid="cauldron-add-to-log"
          className="btn-omni btn-red text-xs uppercase tracking-widest"
        >
          <Sparkles size={12} /> Add to Today's Log
        </button>
      </div>
    </div>
  );
}

/* ============ History Item — collapsed cook entry from /api/cauldron/history ============ */
function HistoryItem({ cook, onAddToLog, onDelete, onToggleFavorite }) {
  const [open, setOpen] = useState(false);
  const recipes = cook.recipes || [];
  const fav = !!cook.favorite;
  let when;
  try {
    when = new Date(cook.ts).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    when = "";
  }
  const titles = recipes.map((r) => r.title).join(" · ");
  return (
    <div
      data-testid={`cauldron-history-item-${cook.id}`}
      className={`bg-[#0A0A0A] border transition-colors ${
        fav
          ? "border-[#D4AF37] shadow-[0_0_18px_rgba(212,175,55,0.15)]"
          : "border-zinc-800 hover:border-[#D4AF37]/60"
      }`}
    >
      <div className="w-full p-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          className="flex-1 min-w-0 text-left"
        >
          <div className="flex items-center gap-2 flex-wrap">
            {fav && (
              <span className="text-[9px] uppercase tracking-widest text-[#D4AF37] inline-flex items-center gap-0.5">
                <Heart size={9} className="fill-[#D4AF37] text-[#D4AF37]" /> Favorite
              </span>
            )}
            {(cook.ingredients || []).slice(0, 5).map((ing, i) => (
              <span
                key={i}
                className="text-[9px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-1.5 py-0.5"
              >
                {ing}
              </span>
            ))}
            {(cook.ingredients || []).length > 5 && (
              <span className="text-[9px] text-neutral-500">+{cook.ingredients.length - 5}</span>
            )}
          </div>
          <div className="text-sm text-white mt-1.5 line-clamp-1">{titles || "Cook"}</div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-0.5">{when}</div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            data-testid={`cauldron-history-fav-${cook.id}`}
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
            aria-label={fav ? "Unfavorite" : "Favorite"}
            aria-pressed={fav}
            className={`p-1 transition-all ${
              fav
                ? "text-[#D4AF37] hover:scale-110"
                : "text-neutral-600 hover:text-[#D4AF37]"
            }`}
            title={fav ? "Remove from favorites" : "Mark as favorite"}
          >
            <Heart size={16} className={fav ? "fill-[#D4AF37]" : ""} />
          </button>
          <button
            type="button"
            onClick={() => setOpen((s) => !s)}
            className="text-[#D4AF37] text-xl leading-none"
            aria-label={open ? "Collapse" : "Expand"}
          >{open ? "▴" : "▾"}</button>
        </div>
      </div>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {recipes.map((r, i) => {
            const m = r.macros_per_serving || {};
            return (
              <div key={i} className="bg-[#0F0F0F] border border-zinc-800 p-2.5">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-display text-base text-white flex items-center gap-1.5 flex-1 min-w-0">
                    <ChefHat size={13} className="text-[#D4AF37] shrink-0" />
                    <span className="truncate">{r.title}</span>
                  </div>
                  <div className="text-[#D4AF37] font-bold text-sm tabular-nums shrink-0">
                    {m.calories || 0} kcal
                  </div>
                </div>
                {r.tagline && (
                  <p className="text-[11px] text-neutral-500 italic mb-1.5">{r.tagline}</p>
                )}
                <div className="flex flex-wrap gap-x-3 text-[9px] uppercase tracking-widest text-neutral-500">
                  <span>⏱ {(r.prep_min || 0) + (r.cook_min || 0)}m</span>
                  <span className="text-red-400">P{m.protein_g || 0}g</span>
                  <span className="text-green-500">C{m.carbs_g || 0}g</span>
                  <span className="text-blue-400">F{m.fat_g || 0}g</span>
                </div>
                {(r.instructions || []).length > 0 && (
                  <ol className="mt-2 space-y-1 text-xs text-neutral-300 list-decimal list-inside">
                    {r.instructions.map((step, j) => (
                      <li key={j} className="leading-snug">{step}</li>
                    ))}
                  </ol>
                )}
                <button
                  type="button"
                  data-testid={`cauldron-history-relog-${cook.id}-${i}`}
                  onClick={() => onAddToLog(r)}
                  className="mt-2 text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-white inline-flex items-center gap-1"
                >
                  <Sparkles size={10} /> Add to today's log
                </button>
              </div>
            );
          })}
          <button
            type="button"
            data-testid={`cauldron-history-delete-${cook.id}`}
            onClick={onDelete}
            className="text-[10px] uppercase tracking-widest text-neutral-600 hover:text-red-400 inline-flex items-center gap-1 mt-1"
          >
            <Trash2 size={10} /> Remove from history
          </button>
        </div>
      )}
    </div>
  );
}

